import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchProduct, fetchProducts } from "../lib/api";
import ProductIcon from "../components/ProductIcon";
import { useDemo } from "../context/DemoContext";
import Seo from "../components/Seo";

export default function ProductDetail() {
  const { slug } = useParams();
  const { openDemo } = useDemo();
  const { t, i18n } = useTranslation();
  const [product, setProduct] = useState(null);
  const [others, setOthers] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null); setNotFound(false);
    fetchProduct(slug).then(setProduct).catch(() => setNotFound(true));
    fetchProducts().then((all) => setOthers(all.filter((p) => p.slug !== slug))).catch(() => {});
  }, [slug, i18n.language]);

  if (notFound) {
    return (
      <div data-testid="product-notfound" className="pt-40 pb-32 text-center px-4">
        <h1 className="font-display font-extrabold text-4xl tracking-tight mb-4">404</h1>
        <Link to="/products" className="text-joyn-orange font-semibold">← {t("common.back")}</Link>
      </div>
    );
  }
  if (!product) return <div className="pt-40 pb-40 text-center text-muted-foreground">{t("common.loading")}</div>;

  const isPortrait = product.slug === "mobile-app";

  return (
    <div data-testid="product-detail-page" className="pt-32 md:pt-40">
      <Seo
        title={`${product.name} — joyn Association Management Software`}
        description={product.long_description || product.description}
        path={`/products/${product.slug}`}
        image={product.image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          description: product.long_description || product.description,
          category: product.category,
          image: product.image,
          brand: { "@type": "Brand", name: "joyn Association Management Software" },
          url: `https://www.joynams.com/products/${product.slug}`,
        }}
      />
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <Link to="/products" data-testid="back-to-products" className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors mb-10">
          <ArrowLeft size={16} /> {t("common.back")}
        </Link>

        <div className="grid lg:grid-cols-2 gap-12 items-center pb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white" style={{ background: product.accent }}><ProductIcon name={product.icon} size={24} /></span>
              <span className="uppercase tracking-[0.16em] text-xs font-bold" style={{ color: product.accent }}>{product.category}</span>
            </div>
            <h1 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight leading-[1.02]">{product.tagline}</h1>
            <p className="mt-5 text-lg text-muted-foreground leading-relaxed max-w-xl">{product.long_description}</p>
            <button data-testid="detail-demo-btn" onClick={openDemo} className="mt-8 inline-flex items-center gap-2 bg-foreground text-white font-semibold px-8 py-4 rounded-full hover:-translate-y-1 transition-transform">{t("detail.demo")} <ArrowRight size={18} /></button>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
            {isPortrait ? (
              <div className="flex items-end justify-center gap-4 rounded-3xl p-8" style={{ background: `linear-gradient(140deg, ${product.accent}22, ${product.accent}0a)` }}>
                {(product.gallery.length ? product.gallery : [product.image]).slice(0, 2).map((g, i) => (
                  <img key={i} src={g} alt={product.name} className={`rounded-2xl shadow-xl border border-border ${i === 1 ? "hidden sm:block translate-y-4" : ""}`} style={{ maxHeight: 420 }} />
                ))}
              </div>
            ) : (
              <div className="rounded-3xl overflow-hidden border border-border shadow-lg"><img src={product.image} alt={product.name} className="w-full h-[300px] md:h-[420px] object-cover" /></div>
            )}
          </motion.div>
        </div>
      </div>

      <section className="bg-secondary/50 border-y border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20">
          <p className="uppercase tracking-[0.2em] text-xs font-bold mb-3" style={{ color: product.accent }}>{t("detail.benefitsEyebrow")}</p>
          <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mb-12">{t("detail.benefitsTitle")}</h2>
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-5">
            {product.benefits.map((b, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05, duration: 0.4 }} className="flex items-start gap-4">
                <span className="mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-lg text-white shrink-0" style={{ background: product.accent }}><Check size={14} /></span>
                <span className="text-lg leading-snug">{b}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20">
        <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mb-10">{t("detail.featuresTitle")}</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {product.features.map((f, i) => (
            <motion.div key={f} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.05, duration: 0.4 }} className="flex items-start gap-3 bg-card border border-border rounded-xl p-5">
              <span className="mt-1 h-2 w-2 rounded-full shrink-0" style={{ background: product.accent }} />
              <span className="font-medium text-sm leading-snug">{f}</span>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-secondary/50 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20">
          <h2 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight mb-10">{t("detail.otherTitle")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {others.slice(0, 3).map((p) => (
              <Link key={p.slug} to={`/products/${p.slug}`} data-testid={`related-product-${p.slug}`} className="group bg-card border border-border rounded-2xl p-7 hover:-translate-y-1 hover:shadow-lg transition-transform">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-white mb-5" style={{ background: p.accent }}><ProductIcon name={p.icon} size={20} /></span>
                <h3 className="font-display font-bold text-lg tracking-tight mb-2">{p.name}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.description}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
